import { createAsyncAction } from 'react-redux-await-control';
import { backoffice } from '~/services';
import { message } from 'antd';

type RequestError = { response?: { status?: number } };

export type CompanyRequestFailure = {
  requestFailed: true;
  httpStatus: number | null;
};

const parseCompanyRequestError = (err: RequestError): CompanyRequestFailure => ({
  requestFailed: true,
  httpStatus: err?.response?.status ?? null,
});

type SearchCompanyContractsParams = {
  cnpj: string;
  flags?: string;
  page?: number;
  size?: number;
};

export const searchCompanyContracts = createAsyncAction('SEARCH_COMPANY_CONTRACTS', {
  saveResult: true,
  initialValue: null,
  context: {
    responseParser: ({ data }) => data,
    errorParser: parseCompanyRequestError,
    onRequestError: (err: RequestError) => {
      if (err?.response?.status === 404) return;

      message.error('Erro ao tentar buscar a empresa');
    },
    request: ({ cnpj, flags = 'CONTRACT_STATUS', page = 0, size = 100 }: SearchCompanyContractsParams) =>
      backoffice.get('/metrics/contracts/search', {
        params: { cnpj, flags, page, size },
      }),
  },
});

export const getCompanyMetrics = createAsyncAction('GET_COMPANY_METRICS', {
  initialValue: null,
  saveResult: true,
  context: {
    onRequestError: () => message.error('Erro ao tentar carregar métricas da empresa'),
    responseParser: ({ data }) => data,
    request: ({ cnpj }) => backoffice.get(`/metrics/employer-operational/cnpj/${cnpj}`),
  },
});

export const getCompanyContracts = createAsyncAction('GET_COMPANY_CONTRACTS', {
  initialValue: null,
  saveResult: true,
  context: {
    onRequestError: () => message.error('Erro ao tentar carregar contratos da empresa'),
    responseParser: ({ data }) => data,
    request: ({ cnpj, size, page }) => backoffice.get(`/metrics/contracts/search`, { params: { cnpj, size, page } }),
  },
});

export const getCompanyContact = createAsyncAction('GET_COMPANY_CONTACTS', {
  initialValue: null,
  saveResult: true,
  context: {
    onRequestError: (err: RequestError) => {
      if (err?.response?.status === 404) return;

      message.error('Erro ao tentar carregar contatos da empresa');
    },
    responseParser: ({ data }) => data,
    errorParser: parseCompanyRequestError,
    request: ({ cnpj }) => backoffice.get(`/company/${cnpj}`),
  },
});

export const getCompanyById = createAsyncAction('GET_COMPANY_BY_ID', {
  initialValue: null,
  saveResult: true,
  context: {
    responseParser: ({ data }) => data,
    errorParser: parseCompanyRequestError,
    onRequestError: (err: RequestError) => {
      if (err?.response?.status === 404) return;

      message.error('Erro ao tentar carregar a empresa');
    },
    request: ({ companyId }) => backoffice.get(`/company/id/${companyId}`),
  },
});

export const deleteCompanyContact = createAsyncAction('DELETE_COMPANY_CONTACTS', {
  initialValue: null,
  saveResult: true,
  context: {
    onRequestError: () => message.error('Erro ao tentar carregar deleta contato da empresa'),
    responseParser: ({ data }) => data,
    request: ({ id }) => backoffice.delete(`/company/contact`, { params: { id } }),
  },
});

export const updateCompanyContact = createAsyncAction('UPDATE_COMPANY_CONTACTS', {
  initialValue: null,
  saveResult: true,
  context: {
    onRequestSuccess: () => message.success('Alterações salvas com sucesso!'),
    onRequestError: () => message.error('Erro ao tentar carregar atualizar contato da empresa'),
    responseParser: ({ data }) => data,
    request: ({ id, name, contact, contactType, valid }) =>
      backoffice.put(`/company/contact`, { id, name, contact, contactType, valid }),
  },
});

export const createContactCompany = createAsyncAction('CREATE_CONTACT_COMPANY', {
  initialValue: null,
  saveResult: true,
  context: {
    onRequestSuccess: () => message.success('Contato adicionado com sucesso!'),
    onRequestError: () => message.error('Erro ao tentar criar contato da empresa'),
    responseParser: ({ data }) => data,
    request: ({ cnpj, name, document, contact, contactType, valid }) =>
      backoffice.post(`/company/${cnpj}/contact`, { name, document, contact, contactType, valid }),
  },
});

export const getCompanyOverduePayments = createAsyncAction('GET_COMPANY_OVERDUE_PAYMENTS', {
  saveResult: true,
  initialValue: [],
  context: {
    errorParser: () => [],
    responseParser: ({ data }) => data,
    onRequestError: () => message.error('Ocorreu um erro ao tentar carregar as parcelas'),
    request: ({ cnpj }) => backoffice.get(`/contracts/document/${cnpj}/overdue/company`),
  },
});
